import type { ErrorDetail, ErrorResponse } from './generated';

export type ApiErrorDetail = ErrorDetail;
export type ApiErrorEnvelope = ErrorResponse;

export class ApiError extends Error {
  public readonly code: string;
  public readonly correlationId: string | null;
  public readonly details: ApiErrorDetail[] | null;
  public readonly status: number;

  public constructor(options: {
    code: string;
    correlationId?: string | null;
    details?: ApiErrorDetail[] | null;
    message: string;
    status: number;
  }) {
    super(options.message);
    this.name = 'ApiError';
    this.code = options.code;
    this.correlationId = options.correlationId ?? null;
    this.details = options.details ?? null;
    this.status = options.status;
  }
}

type FetchImplementation = typeof fetch;

export interface ApiClientOptions {
  basePath?: string;
  cookieSource?: () => string;
  fetchImplementation?: FetchImplementation;
}

export interface ApiRequestOptions
  extends Omit<RequestInit, 'body' | 'method'> {
  body?: BodyInit | null;
  json?: unknown;
  /** Only the three explicitly public authentication POSTs may opt out of CSRF. */
  publicAuth?: boolean;
  session?: boolean;
}

const AUTH_CSRF_COOKIE_NAMES = [
  '__Host-flow-wizard-csrf',
  'flow_wizard_csrf',
] as const;

const PUBLIC_AUTH_POST_PATHS = new Set([
  '/auth/login',
  '/auth/password-reset/request',
  '/auth/password-reset/confirm',
]);

function normalizeBasePath(basePath: string) {
  if (/^https?:\/\//iu.test(basePath)) {
    throw new Error('The API base path must be same-origin.');
  }

  const withLeadingSlash = basePath.startsWith('/') ? basePath : `/${basePath}`;
  return withLeadingSlash.replace(/\/$/u, '');
}

function normalizeRequestPath(path: string) {
  if (/^https?:\/\//iu.test(path)) {
    throw new Error('API request paths must be same-origin.');
  }

  return path.startsWith('/') ? path : `/${path}`;
}

function isUnknownArray(value: unknown): value is unknown[] {
  return Array.isArray(value);
}

function isApiErrorDetail(value: unknown): value is ApiErrorDetail {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const detail = value as Record<string, unknown>;
  return (
    typeof detail.issue_type === 'string' &&
    typeof detail.reason === 'string' &&
    isUnknownArray(detail.location) &&
    detail.location.every(
      (part) => typeof part === 'string' || typeof part === 'number',
    )
  );
}

function isApiErrorEnvelope(value: unknown): value is ApiErrorEnvelope {
  if (typeof value !== 'object' || value === null || !('error' in value)) {
    return false;
  }

  const error = value.error;
  if (typeof error !== 'object' || error === null) {
    return false;
  }

  const errorRecord = error as Record<string, unknown>;
  const details = errorRecord.details;
  const detailsAreValid =
    details === undefined ||
    details === null ||
    (isUnknownArray(details) && details.every(isApiErrorDetail));

  return (
    typeof errorRecord.code === 'string' &&
    typeof errorRecord.message === 'string' &&
    typeof errorRecord.correlation_id === 'string' &&
    detailsAreValid
  );
}

async function readResponseBody(response: Response): Promise<unknown> {
  if (response.status === 204) {
    return undefined;
  }

  const text = await response.text();
  if (text.length === 0) {
    return undefined;
  }

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

function readNamedCookie(cookieHeader: string, name: string): string | null {
  for (const part of cookieHeader.split(';')) {
    const separator = part.indexOf('=');
    if (separator < 0 || part.slice(0, separator).trim() !== name) continue;
    const value = part.slice(separator + 1).trim();
    if (value.length === 0) return null;
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }
  return null;
}

function browserCookieSource(): string {
  return typeof document === 'undefined' ? '' : document.cookie;
}

type UnauthorizedHandler = () => void;
export interface SessionExpiries {
  absoluteExpiresAt: string;
  idleExpiresAt: string;
}
type SessionRefreshHandler = (expiries: SessionExpiries) => void;

export class ApiClient {
  private readonly basePath: string;
  private readonly cookieSource: () => string;
  private readonly fetchImplementation: FetchImplementation;
  private csrfToken: string | null = null;
  private sessionRequest: Promise<string> | null = null;
  private readonly unauthorizedHandlers = new Set<UnauthorizedHandler>();
  private readonly sessionRefreshHandlers = new Set<SessionRefreshHandler>();

  public constructor({
    basePath = '/api/v1',
    cookieSource = browserCookieSource,
    fetchImplementation,
  }: ApiClientOptions = {}) {
    this.basePath = normalizeBasePath(basePath);
    this.cookieSource = cookieSource;
    this.fetchImplementation = fetchImplementation ?? globalThis.fetch.bind(globalThis);
  }

  public clearCsrfToken(): void {
    this.csrfToken = null;
  }

  public onUnauthorized(handler: UnauthorizedHandler): () => void {
    this.unauthorizedHandlers.add(handler);
    return () => this.unauthorizedHandlers.delete(handler);
  }

  public onSessionRefresh(handler: SessionRefreshHandler): () => void {
    this.sessionRefreshHandlers.add(handler);
    return () => this.sessionRefreshHandlers.delete(handler);
  }

  public seedCsrfToken(token: string): void {
    if (token.length === 0) throw new Error('The CSRF token must not be empty.');
    this.csrfToken = token;
  }

  public get<T>(path: string, options?: ApiRequestOptions) {
    return this.request<T>('GET', path, options);
  }

  public post<T>(path: string, options?: ApiRequestOptions) {
    return this.request<T>('POST', path, options);
  }

  public put<T>(path: string, options?: ApiRequestOptions) {
    return this.request<T>('PUT', path, options);
  }

  public patch<T>(path: string, options?: ApiRequestOptions) {
    return this.request<T>('PATCH', path, options);
  }

  public delete<T>(path: string, options?: ApiRequestOptions) {
    return this.request<T>('DELETE', path, options);
  }

  private async ensureSession(): Promise<string> {
    if (this.csrfToken !== null) return this.csrfToken;
    const cookieHeader = this.cookieSource();
    for (const cookieName of AUTH_CSRF_COOKIE_NAMES) {
      const cookieToken = readNamedCookie(cookieHeader, cookieName);
      if (cookieToken !== null) {
        this.csrfToken = cookieToken;
        return cookieToken;
      }
    }
    if (this.sessionRequest !== null) return this.sessionRequest;
    this.sessionRequest = (async () => {
      let response: Response;
      try {
        response = await this.fetchImplementation(`${this.basePath}/session`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          method: 'GET',
        });
      } catch {
        throw new ApiError({
          code: 'network_error',
          message: 'The local Flow Wizard session could not be established.',
          status: 0,
        });
      }
      const body = await readResponseBody(response);
      if (
        !response.ok ||
        typeof body !== 'object' ||
        body === null ||
        !('csrf_token' in body) ||
        typeof body.csrf_token !== 'string'
      ) {
        throw new ApiError({
          code: 'session_required',
          message: 'The local Flow Wizard session could not be established.',
          status: response.status,
        });
      }
      this.csrfToken = body.csrf_token;
      return body.csrf_token;
    })();
    try {
      return await this.sessionRequest;
    } finally {
      this.sessionRequest = null;
    }
  }

  public async request<T>(
    method: string,
    path: string,
    options: ApiRequestOptions = {},
  ): Promise<T> {
    const {
      body: providedBody,
      headers: providedHeaders,
      json,
      publicAuth = false,
      session = false,
      ...requestOptions
    } = options;
    if (json !== undefined && providedBody !== undefined) {
      throw new Error('Provide either json or body, not both.');
    }
    const headers = new Headers(providedHeaders);
    headers.set('Accept', 'application/json');

    if (json !== undefined) {
      headers.set('Content-Type', 'application/json');
    }
    const normalizedMethod = method.toUpperCase();
    const normalizedPath = normalizeRequestPath(path);
    const unsafe = !['GET', 'HEAD', 'OPTIONS'].includes(normalizedMethod);
    if (
      publicAuth &&
      (normalizedMethod !== 'POST' || !PUBLIC_AUTH_POST_PATHS.has(normalizedPath))
    ) {
      throw new Error(
        'Only public authentication POST requests may omit CSRF protection.',
      );
    }
    if ((unsafe || session) && !publicAuth) {
      headers.set('X-Flow-Wizard-CSRF', await this.ensureSession());
    }

    let response: Response;
    try {
      const requestInit: RequestInit = {
        ...requestOptions,
        method,
        headers,
        credentials: 'same-origin',
      };

      if (json !== undefined) {
        requestInit.body = JSON.stringify(json);
      } else if (providedBody !== undefined) {
        requestInit.body = providedBody;
      }

      response = await this.fetchImplementation(
        `${this.basePath}${normalizedPath}`,
        requestInit,
      );
    } catch {
      throw new ApiError({
        code: 'network_error',
        message: 'The local Flow Wizard service could not be reached.',
        status: 0,
      });
    }

    const body = await readResponseBody(response);
    const idleExpiresAt = response.headers.get(
      'X-Flow-Wizard-Session-Idle-Expires-At',
    );
    const absoluteExpiresAt = response.headers.get(
      'X-Flow-Wizard-Session-Absolute-Expires-At',
    );
    if (
      response.ok &&
      idleExpiresAt !== null &&
      absoluteExpiresAt !== null &&
      Number.isFinite(Date.parse(idleExpiresAt)) &&
      Number.isFinite(Date.parse(absoluteExpiresAt))
    ) {
      for (const handler of this.sessionRefreshHandlers) {
        handler({ absoluteExpiresAt, idleExpiresAt });
      }
    }
    if (response.status === 401) {
      this.clearCsrfToken();
      for (const handler of this.unauthorizedHandlers) handler();
    }

    if (!response.ok) {
      if (isApiErrorEnvelope(body)) {
        throw new ApiError({
          code: body.error.code,
          message: body.error.message,
          status: response.status,
          correlationId: body.error.correlation_id,
          details: body.error.details ?? null,
        });
      }

      throw new ApiError({
        code: 'http_error',
        message: `The local Flow Wizard service returned HTTP ${response.status}.`,
        status: response.status,
        correlationId: response.headers.get('x-correlation-id'),
      });
    }

    if (
      publicAuth &&
      normalizedPath === '/auth/login' &&
      typeof body === 'object' &&
      body !== null &&
      'csrf_token' in body &&
      typeof body.csrf_token === 'string'
    ) {
      this.seedCsrfToken(body.csrf_token);
    }

    return body as T;
  }
}

export const apiClient = new ApiClient();
